import { marketPairs, trendScenarios } from '@/mocks/market';

export interface ForexPair {
  symbol: string;
  name: string;
  price: string;
  spread: string;
  pip: string;
  trend: string;
  trendUp: boolean;
  change: string;
  series: number[];
  dates: string[];
  digits: number;
}

/**
 * Fixed example dates used by every sparkline on the site.
 * The site is a static handbook, so the chart labels never depend on the
 * visitor's clock and never pretend to be a live feed.
 */
const EXAMPLE_DATES = [
  '2026-09-01',
  '2026-09-02',
  '2026-09-03',
  '2026-09-04',
  '2026-09-07',
  '2026-09-08',
  '2026-09-09',
  '2026-09-10',
  '2026-09-11',
  '2026-09-14',
  '2026-09-15',
  '2026-09-16',
  '2026-09-17',
  '2026-09-18',
  '2026-09-21',
  '2026-09-22',
  '2026-09-23',
  '2026-09-24',
  '2026-09-25',
  '2026-09-28',
  '2026-09-29',
  '2026-09-30',
  '2026-10-01',
  '2026-10-02',
];

const POINT_COUNT = EXAMPLE_DATES.length;

const buildSeries = (target: number, trendUp: boolean): number[] => {
  const amplitude = target * 0.012;
  const direction = trendUp ? 1 : -1;
  const series = Array.from({ length: POINT_COUNT }, (_, index) => {
    const progress = index / (POINT_COUNT - 1);
    const base = target - direction * amplitude * (1 - progress);
    const wave = Math.sin(progress * Math.PI * 3) * amplitude * 0.45;
    const jitter = Math.sin(index * 2.3) * amplitude * 0.12;
    return base + wave + jitter;
  });
  series[series.length - 1] = target;
  return series;
};

const getDigits = (price: string): number => {
  const decimals = price.split('.')[1];
  return decimals ? decimals.length : 0;
};

/**
 * Static reference pairs. No network request, no auto refresh, no timestamps:
 * the numbers are fixed examples that only exist to explain how quotes work.
 */
export function getReferencePairs(): ForexPair[] {
  return marketPairs.map((pair) => {
    const target = Number(pair.price.replace(/,/g, ''));
    return {
      ...pair,
      series: buildSeries(target, pair.trendUp),
      dates: EXAMPLE_DATES,
      digits: getDigits(pair.price),
    };
  });
}

export interface TrendScenario {
  id: string;
  tab: string;
  symbol: string;
  name: string;
  price: string;
  change: string;
  spread: string;
  pip: string;
  trend: string;
  trendUp: boolean;
  tone: 'up' | 'flat' | 'down';
  series: number[];
  points: string[];
  digits: number;
  dates: string[];
}

/**
 * Three fixed reading cases (uptrend / range / downtrend) shown on the
 * homepage. Values are hard coded examples, nothing is fetched or refreshed.
 */
export function getTrendScenarios(): TrendScenario[] {
  return trendScenarios.map((scenario) => ({
    ...scenario,
    tone: scenario.tone as TrendScenario['tone'],
    digits: getDigits(scenario.price),
    dates: EXAMPLE_DATES.slice(0, scenario.series.length),
  }));
}

export function getStaticPairs(symbols: string[]): ForexPair[] {
  return getReferencePairs().filter((pair) => symbols.includes(pair.symbol));
}

export function getMetalSymbols(): string[] {
  return ['XAU/USD'];
}